import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { PhoneOff, Clock, UserX, ThumbsDown } from "lucide-react";

const problems = [
{
  icon: PhoneOff,
  title: "Missed Calls = Lost Revenue",
  description: "Every unanswered call is $500\u2013$2,000 walking straight to your competitor. You're on a job, they're stealing yours.",
  stat: "$2,000+",
  statLabel: "lost per missed call"
},
{
  icon: Clock,
  title: "Slow Callbacks Kill Deals",
  description: "Call back in an hour? They've already booked someone else. Speed wins in plumbing, and you're losing the race.",
  stat: "78%",
  statLabel: "book whoever calls first"
},
{
  icon: UserX,
  title: "Tire-Kickers Waste Your Day",
  description: "You drive 30 minutes for a quote and they vanish. Without qualification, you're burning hours on dead leads.",
  stat: "4 hrs",
  statLabel: "wasted weekly on bad leads"
},
{
  icon: ThumbsDown,
  title: "Bad Reviews Sink Your Business",
  description: "One angry review on Google costs you 30 potential customers. And you never even asked the happy ones to leave a review.",
  stat: "30x",
  statLabel: "impact of a single bad review"
}];


export default function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ backgroundColor: "#0A0A0A" }}>

      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16">

          <span
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B8DEF] font-medium mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="problem-label">

            <span className="w-6 h-px bg-[#5B8DEF]"></span>
            The Problem
            <span className="w-6 h-px bg-[#5B8DEF]"></span>
          </span>
          <h2
            className="text-3xl md:text-4xl tracking-tight font-semibold text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="problem-headline">

            You're Bleeding Money
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          {problems.map((problem, i) =>
          <motion.div
            key={problem.title}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
            data-testid={`problem-card-${i}`}
            className="group bg-[#121212] border border-white/[0.06] rounded-lg p-7 hover:border-white/[0.12] hover:bg-[#141414] transition-all duration-300">

              <div className="flex items-start gap-5">
                <div className="w-11 h-11 rounded-lg bg-[#5B8DEF]/10 flex items-center justify-center flex-shrink-0">
                  <problem.icon className="w-5 h-5 text-[#5B8DEF]" />
                </div>
                <div className="flex-1">
                  <h3
                  className="text-lg font-semibold text-white mb-2"
                  style={{ fontFamily: "Outfit, sans-serif" }}>

                    {problem.title}
                  </h3>
                  <p
                  className="text-sm text-[#A3A3A3] leading-relaxed mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}>

                    {problem.description}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {problem.stat}
                    </span>
                    <span className="text-xs text-[#A3A3A3] uppercase tracking-wide" style={{ fontFamily: "Manrope, sans-serif" }}>
                      {problem.statLabel}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>);

}